import React, { useState, useEffect, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion, useScroll, useTransform, useSpring } from 'framer-motion';
import { ArrowLeft, ChevronLeft, ChevronRight, X } from 'lucide-react';
import { projects } from '@/projects';
import OptimizedImage from '../ui/OptimizedImage';
import { useTranslation } from 'react-i18next';
import { formatArea, formatFloors, getLocalizedText, normalizeLang, sanitizeText } from '@/lib/format';
import 'react-medium-image-zoom/dist/styles.css';

const isVideo = (url: string) => /\.(mp4|webm)$/i.test(url);

const ProjectDetail = () => {
  const { t, i18n } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const projectId = parseInt(id || '1', 10);
  const project = projects.find((p) => p.info.id === projectId);
  const lang = normalizeLang(i18n.language);

  const [currentIndex, setCurrentIndex] = useState(0);
  const [showLightbox, setShowLightbox] = useState(false);
  const [isTransitioning, setIsTransitioning] = useState(false);

  const { scrollY } = useScroll();
  const scrollSpring = useSpring(scrollY, { stiffness: 80, damping: 20 });

  const infoSectionRef = useRef<HTMLDivElement>(null);
  const lightboxRef = useRef<HTMLDivElement>(null);

  const blur = useTransform(scrollSpring, [0, 800], ['blur(0px)', 'blur(8px)']);
  const blackOverlayOpacity = useTransform(scrollSpring, [0, 900], [0, 0.25]);
  const [infoTop, setInfoTop] = useState(600);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'auto' });
    const updateInfoTop = () => {
      if (infoSectionRef.current) {
        setInfoTop(infoSectionRef.current.offsetTop - 150);
      }
    };
    updateInfoTop();
    window.addEventListener('resize', updateInfoTop);
    return () => window.removeEventListener('resize', updateInfoTop);
  }, []);

  const titleTranslateY = useTransform(scrollY, [0, infoTop], ['150px', `${infoTop - 100}px`]);
  const titleScale = useTransform(scrollY, [0, infoTop], [1, 1]);

  const formatValue = (value?: string | number) => {
    if (typeof value === 'number') {
      return new Intl.NumberFormat(i18n.language).format(value);
    }
    return sanitizeText(value || '');
  };

  const changeImage = (direction: 'prev' | 'next') => {
    if (isTransitioning) return;
    setIsTransitioning(true);
    setCurrentIndex((prev) => {
      const len = project?.assets.images.length || 0;
      if (len === 0) return prev;
      return direction === 'next' ? (prev + 1) % len : (prev - 1 + len) % len;
    });
    setTimeout(() => setIsTransitioning(false), 300);
  };

  const handleLightboxClick = (e: React.MouseEvent) => {
    if (e.target === lightboxRef.current) setShowLightbox(false);
  };

  if (!project) {
    return <div className="text-center py-20 text-white">{t('common.projectNotFound')}</div>;
  }

  const description = getLocalizedText(project.info.description, lang);
  const category = getLocalizedText(project.info.category, lang);
  const floorsValue = formatFloors(project.info.floor, lang);
  const buildingValue = formatArea(project.info.building, lang);
  const detailRows = [
    { label: t('projectDetail.info.year'), value: formatValue(project.info.year) },
    { label: t('projectDetail.info.location'), value: formatValue(project.info.location) },
    { label: t('projectDetail.info.category'), value: formatValue(category) },
    floorsValue ? { label: t('projectDetail.info.floors'), value: floorsValue } : null,
    buildingValue ? { label: t('projectDetail.info.building'), value: buildingValue } : null
  ].filter(Boolean) as { label: string; value: string | number }[];

  const images = project.assets.images;
  const activeAsset = images[currentIndex];

  return (
    <div className="relative flex flex-col overflow-hidden">
      <motion.div
        className="fixed inset-0 -z-30 bg-center bg-cover"
        style={{ backgroundImage: `url(${project.info.cover})`, filter: blur }}
      />
      <motion.div className="fixed inset-0 -z-20 bg-black opacity-[0.15]" />
      <motion.div className="fixed inset-0 -z-10 bg-black" style={{ opacity: blackOverlayOpacity }} />

      <div className="container-custom relative z-10 pt-32 sm:pt-40 min-h-screen">
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1 }}
          className="mb-8"
        >
          <Link
            to="/projects"
            className="inline-flex items-center text-[#D9D9D9] hover:text-[#C8102E] transition-colors"
          >
            <ArrowLeft size={20} className="mr-2" />
            {t('projectDetail.back')}
          </Link>
        </motion.div>

        <motion.h1
          className="text-5xl sm:text-6xl md:text-7xl font-semibold tracking-tight text-center text-[#D9D9D9] font-[Poppins]"
          style={{ y: titleTranslateY, scale: titleScale }}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1 }}
        >
          {project.info.title}
        </motion.h1>
      </div>

      <div className="h-[75vh]" />

      <div ref={infoSectionRef} className="container-custom flex flex-col lg:flex-row justify-between gap-12 py-20">
        <div className="flex-1 space-y-4 text-[#D9D9D9] text-base sm:text-lg leading-relaxed max-w-2xl">
          <p>{description ? sanitizeText(description) : t('projectDetail.descriptionFallback')}</p>
        </div>

        <div className="flex-1 space-y-4 text-sm sm:text-base text-[#C0C0C0]">
          {detailRows.map((item, index) => (
            <div key={index} className="flex flex-wrap sm:flex-nowrap justify-between gap-2 sm:gap-4">
              <span className="min-w-[120px] text-left">{item.label}:</span>
              <span className="flex-1 text-left break-words">{item.value}</span>
            </div>
          ))}
        </div>
      </div>

      <div className="container-custom pb-20">
        <motion.h2
          className="text-3xl font-light text-center mb-10 text-[#D9D9D9]"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          {t('projectDetail.gallery')}
        </motion.h2>

        <div className="relative w-full max-w-5xl mx-auto mb-8">
          <div className="relative w-full aspect-video overflow-hidden">
            <button onClick={() => setShowLightbox(true)} className="w-full h-full flex justify-center items-center">
              {activeAsset && isVideo(activeAsset.url) ? (
                <video
                  src={activeAsset.url}
                  muted
                  playsInline
                  preload="metadata"
                  controls={false}
                  className="w-full h-full object-cover"
                />
              ) : (
                <OptimizedImage
                  src={activeAsset?.url || ''}
                  alt={t('projectDetail.imageAlt', { index: currentIndex + 1 })}
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
              )}
            </button>

            <button
              onClick={() => changeImage('prev')}
              className="absolute left-4 top-1/2 -translate-y-1/2 text-white bg-black/50 p-2 rounded-full hover:bg-black"
              aria-label={t('explorer.back')}
            >
              <ChevronLeft size={24} />
            </button>
            <button
              onClick={() => changeImage('next')}
              className="absolute right-4 top-1/2 -translate-y-1/2 text-white bg-black/50 p-2 rounded-full hover:bg-black"
              aria-label={t('explorer.forward')}
            >
              <ChevronRight size={24} />
            </button>
          </div>
        </div>

        <div className="flex justify-center flex-wrap gap-4">
          {images.map((asset, idx) => (
            <button
              key={idx}
              onClick={() => setCurrentIndex(idx)}
              className={`w-20 h-20 overflow-hidden ${currentIndex === idx ? 'ring-2 ring-[#C8102E]' : ''}`}
              aria-label={t('projectDetail.thumbAlt', { index: idx + 1 })}
            >
              {isVideo(asset.url) ? (
                <video src={asset.url} muted playsInline preload="metadata" className="w-full h-full object-cover" />
              ) : (
                <OptimizedImage
                  src={asset.url}
                  alt={t('projectDetail.thumbAlt', { index: idx + 1 })}
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
              )}
            </button>
          ))}
        </div>
      </div>

      {showLightbox && (
        <div
          className="fixed inset-0 bg-black/90 z-[9999] flex items-center justify-center safe-area-top safe-area-bottom"
          onClick={handleLightboxClick}
          ref={lightboxRef}
        >
          <button
            onClick={() => setShowLightbox(false)}
            className="absolute top-4 right-4 text-white"
            aria-label={t('common.close')}
          >
            <X size={32} />
          </button>
          <button
            onClick={() => changeImage('prev')}
            className="absolute left-4 text-white"
            aria-label={t('explorer.back')}
          >
            <ChevronLeft size={32} />
          </button>
          <div className="max-w-full max-h-[80vh] flex items-center justify-center">
            {activeAsset && isVideo(activeAsset.url) ? (
              <video
                src={activeAsset.url}
                controls
                playsInline
                preload="metadata"
                className="max-w-full max-h-[80vh] object-contain"
              />
            ) : (
              <img
                src={activeAsset?.url || ''}
                alt={t('projectDetail.imageAlt', { index: currentIndex + 1 })}
                className="max-w-full max-h-[80vh] object-contain"
                loading="lazy"
              />
            )}
          </div>
          <button
            onClick={() => changeImage('next')}
            className="absolute right-4 text-white"
            aria-label={t('explorer.forward')}
          >
            <ChevronRight size={32} />
          </button>
        </div>
      )}
    </div>
  );
};

export default ProjectDetail;
